import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import AddFilterPanel from './AddFilterPanel';

/**
 * Toolbar [+ Add filter] button + panel, isolated as its own MobX observer.
 *
 * TD-2626 perf: SearchResults / Projects tab views are huge observers. Reading
 * `isAddFilterPanelOpen` there forced a full page re-render on every open
 * (results table + chrome) before the panel could paint — felt like ~1s.
 * This component owns that flag so opening only re-renders the anchor/panel.
 */
const AddFilterAnchor = observer(
  ({
    store,
    variant = 'desktop',
    disabled = false,
    harmonized = false,
    iconOnly = false,
    className
  }) => {
    const open = store.isAddFilterPanelOpen;
    const isMobile = variant === 'mobile';
    const anchorClass =
      className ||
      (isMobile ? 'add-filter-anchor add-filter-anchor--mobile' : 'add-filter-anchor');

    let buttonProps;
    if (isMobile || iconOnly) {
      buttonProps = {
        tooltipText: open ? '' : 'Add filter',
        tooltipPosition: 'bottom-center',
        wrapClassName: 'add-filter-trigger'
      };
    } else if (harmonized) {
      buttonProps = {
        innerText: 'Add filter',
        wrapClassName: 'add-filter-trigger pv-toolbar-text-button'
      };
    } else {
      buttonProps = {
        innerText: 'Add filter',
        wrapClassName: 'filter-button add-filter-trigger'
      };
    }

    return (
      <div className={anchorClass}>
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          icon={ICON_BUTTON_ICONS.FUNNEL}
          {...buttonProps}
          onClick={() =>
            open ? store.closeAddFilterPanel() : store.openAddFilterPanel()
          }
          filled={open}
          disabled={disabled}
        />
        {/* TD-2626 D: mount only when layout matches — avoids dual DOM. */}
        {store.layout === variant && <AddFilterPanel store={store} variant={variant} />}
      </div>
    );
  }
);

AddFilterAnchor.propTypes = {
  store: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['desktop', 'mobile']),
  disabled: PropTypes.bool,
  /** Projects toolbar uses text-button styling when harmonized. */
  harmonized: PropTypes.bool,
  /** Icon-only trigger (Projects non-harmonized toolbar). */
  iconOnly: PropTypes.bool,
  className: PropTypes.string
};

export default AddFilterAnchor;
